/**
 * Testimonial Carousel Block JavaScript
 * Sets CSS animation duration - content duplicated in Liquid for infinite scroll
 * Following badge carousel approach (CSS-only animation)
 * 
 * @param {HTMLElement} container - The main section container element.
 */
class TestimonialCarousel {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    this.track = this.container.querySelector('.testimonial-carousel__track');
    this.resizeObserver = null;
    this.videoObserver = null;

    this.settings = this.getSettings();
    this.init();
  }

  getSettings() {
    return {
      scrollDuration: parseFloat(this.container.dataset.scrollDuration || '30')
    };
  }

  init() {
    this.setupCarousel();
    this.bindEvents();
    this.setupVideoAutoplay();
  }

  setupCarousel() {
    if (!this.track) return;

    // Get all testimonial cards
    const cards = this.track.querySelectorAll('.testimonial-card');
    const totalCards = cards.length / 3; // Since we triplicate in Liquid
    
    if (totalCards === 0) return;
    
    // Calculate the width of one complete set
    let totalWidth = 0;
    
    // Get the computed style to read the actual margin
    const containerStyles = getComputedStyle(this.container);
    const cardSpacing = parseFloat(
      containerStyles.getPropertyValue('--testimonial-card-spacing') || '18'
    );
    
    // Measure each card in the first set
    for (let i = 0; i < totalCards; i++) {
      const card = cards[i];
      if (card instanceof HTMLElement) {
        totalWidth += card.offsetWidth;
        // Add margin for all cards except the last one
        if (i < totalCards - 1) {
          totalWidth += cardSpacing;
        }
      }
    }
    
    // Set the precise scroll distance
    this.container.style.setProperty('--scroll-distance', `${totalWidth}px`);
    
    // Set CSS animation duration (no cloning needed - done in Liquid)
    this.container.style.setProperty('--testimonial-scroll-duration', `${this.settings.scrollDuration}s`);
  }

  /**
   * Force-plays testimonial videos as they scroll into view and pauses them
   * when they scroll out. Needed because iOS Safari does not reliably honor
   * the `autoplay` attribute for <video> elements that enter the viewport via
   * a CSS transform animation (the infinite-scroll track) rather than a
   * native page load/scroll - it wants play() called while the element is
   * actually visible. Also covers the Liquid-triplicated duplicate cards,
   * since querySelectorAll picks up every copy in the track, and pausing
   * off-screen copies keeps us from running 3x the videos at once on mobile.
   */
  setupVideoAutoplay() {
    if (typeof IntersectionObserver === 'undefined') return;

    // Re-scanning (e.g. theme editor section:load refresh) - drop the old
    // observer first so we don't double-observe existing videos.
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }

    const videos = this.container.querySelectorAll('.testimonial-card__video');
    if (!videos.length) return;

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;

          if (entry.isIntersecting) {
            // BRIEF-022: Helix ships these videos with preload="none" so
            // off-screen copies (including the Liquid-triplicated ones)
            // never fetch a byte - only the poster shows until something
            // triggers a load. Calling play() alone is spec'd to trigger
            // that load, but bumping preload to "auto" on just this
            // on-screen video first makes the fetch-and-play start
            // reliably across browsers instead of depending on play()'s
            // implicit-load behavior. Off-screen copies are left at
            // "none" - only videos that actually scroll into view ever
            // download data.
            if (video.preload !== 'auto') {
              video.preload = 'auto';
            }
            const playPromise = video.play();
            if (playPromise !== undefined) {
              // Autoplay can still be rejected (e.g. iOS Low Power Mode).
              // Nothing overrides that - leave the poster frame showing,
              // no custom play button.
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      },
      {
        threshold: 0.25,
      }
    );

    videos.forEach((video) => this.videoObserver.observe(video));
  }

  bindEvents() {
    if (!this.track) return;
    
    // Set up resize observer to recalculate on size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.setupCarousel();
    });
    this.resizeObserver.observe(this.container);
    
    // Handle theme editor events
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element && event.target.contains(this.container)) {
        this.refresh();
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target instanceof Element && event.target.contains(this.container)) {
        this.destroy();
      }
    });
  }

  refresh() {
    this.settings = this.getSettings();
    this.setupCarousel();
    this.setupVideoAutoplay();
  }

  destroy() {
    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clean up video intersection observer
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }
  }
}

// Initialize testimonial carousels when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const carousels = document.querySelectorAll('[data-section-type="testimonial-carousel"]');
  
  carousels.forEach((carousel) => {
    if (carousel instanceof HTMLElement) {
      new TestimonialCarousel(carousel);
    }
  });
});

// Handle dynamic loading (for theme editor)
document.addEventListener('shopify:section:load', function(event) {
  if (event.target instanceof Element) {
    const carousel = event.target.querySelector('[data-section-type="testimonial-carousel"]');
    if (carousel instanceof HTMLElement) {
      new TestimonialCarousel(carousel);
    }
  }
}); 